<template>
  <div class="feedback-page cabinet-page" :class="{ 'is-loading': loading }">
    <cabinet-page-header :eyebrow="$t(isIdea ? 'feedback.improve' : 'feedback.confidential')" :title="$t(isIdea ? 'feedback.ideaTitle' : 'feedback.misconductTitle')" :subtitle="$t(isIdea ? 'feedback.ideaSubtitle' : 'feedback.misconductSubtitle')" />
    <div class="feedback-page__notice">
      {{ $t(isIdea ? 'feedback.ideaNotice' : 'feedback.misconductNotice') }}
    </div>
    <app-button class="btn--stretch feedback-page__action" theme="request" @click.native="openForm">{{ $t(isIdea ? 'feedback.submitIdea' : 'feedback.reportMisconduct') }}</app-button>
    <section class="feedback-history">
      <h2>{{ $t(isIdea ? 'feedback.yourIdeas' : 'feedback.yourReports') }}</h2>
      <app-page-loader v-if="loading" />
      <cabinet-state v-else-if="loadError" type="error" :title="$t('feedback.historyUnavailable')" @retry="loadHistory" />
      <cabinet-state
        v-else-if="!items.length"
        type="empty"
        :title="$t(isIdea ? 'feedback.noIdeas' : 'feedback.noReports')"
        :message="$t(isIdea ? 'feedback.ideasAppear' : 'feedback.reportsAppear')"
      />
      <router-link v-for="item in items" v-else :key="item.id" :to="detailRoute(item)" class="feedback-card">
        <div><cabinet-status :status="item.status" /><small>{{ formatDate(item.created_at) }}</small></div>
        <strong v-if="!isIdea">{{ categoryName(item.category) }}</strong><p>{{ item.text }}</p><span>{{ $t('common.viewDetails') }}</span>
      </router-link>
    </section>
    <modal
      :name="modalName"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      :shiftY="0"
      transition="pop-out"
      @closed="onFormClosed"
    >
      <div class="popup">
        <button class="popup__close" type="button" :aria-label="$t('common.close')" @click="closeForm"></button>
        <div class="popup__title">{{ $t(isIdea ? 'feedback.submitIdea' : 'feedback.misconductTitle') }}</div>
        <div class="popup__body">
          <form class="feedback-form" @submit.prevent="submit">
            <app-form-group v-if="!isIdea" :label="$t('feedback.category')" required>
              <v-select
                v-localized-select-label="$t('feedback.chooseCategory')"
                v-model="form.category"
                :options="categories"
                :reduce="category => category.code"
                :clearable="false"
                :searchable="false"
                label="name"
                :placeholder="$t('feedback.chooseCategory')"
                class="select"
                :class="{ 'select--error': attempted && !form.category }"
              >
                <template #open-indicator>
                  <svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5" fill="none">
                    <path d="M7.872.418 7.61.138a.423.423 0 0 0-.619 0l-2.99 3.164L1.01.135a.422.422 0 0 0-.619 0L.128.413a.483.483 0 0 0 0 .655L3.691 4.85a.437.437 0 0 0 .31.149h.002a.437.437 0 0 0 .31-.149l3.56-3.773A.482.482 0 0 0 8 .747a.477.477 0 0 0-.128-.33Z" fill="#606060"/>
                  </svg>
                </template>
                <template #option="category"><div class="select__item">{{ category.name }}</div></template>
                <template #selected-option="category"><div class="selected">{{ category.name }}</div></template>
              </v-select>
              <template #error><div v-if="attempted && !form.category">{{ $t('common.required') }}</div></template>
            </app-form-group>
            <app-form-group :label="$t(isIdea ? 'feedback.suggestion' : 'feedback.happened')" required>
              <app-textarea v-model="form.text" :placeholder="$t(isIdea ? 'feedback.shareIdea' : 'feedback.describe')" :error="attempted && !form.text" />
              <template #error><div v-if="attempted && !form.text">{{ $t('common.required') }}</div></template>
            </app-form-group>
            <app-button ref="submit" class="btn--stretch" theme="request" type="submit" :disabled="submitting">{{ $t(isIdea ? 'feedback.submitIdea' : 'feedback.submitReport') }}</app-button>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import { getPersonalCabinetFeedback, postPersonalCabinetFeedback } from '@/http'
import { formatLocalizedDate } from '@/i18n'
import CabinetPageHeader from './CabinetPageHeader'
import CabinetState from './CabinetState'
import CabinetStatus from './CabinetStatus'

export default {
  name: 'PersonalCabinetFeedback',
  components: { CabinetPageHeader, CabinetState, CabinetStatus },
  props: { kind: { type: String, required: true } },
  data() { return { form: { category: '', text: '' }, attempted: false, submitting: false, loading: true, loadError: false, items: [], categoryCodes: ['colleagues', 'management', 'working_conditions', 'other'] } },
  computed: {
    isIdea() { return this.kind === 'idea' },
    valid() { return this.form.text && (this.isIdea || this.form.category) },
    modalName() { return `personal-cabinet-${this.kind}-form` },
    categories() { return this.categoryCodes.map(code => ({ code, name: this.$t(`categories.${code}`) })) },
  },
  watch: { kind() { this.form = { category: '', text: '' }; this.loadHistory() } },
  created() { this.loadHistory() },
  methods: {
    openForm() {
      this.form = { category: '', text: '' }
      this.attempted = false
      this.$nextTick(() => this.$modal.show(this.modalName))
    },
    closeForm() { this.$modal.hide(this.modalName) },
    onFormClosed() { this.attempted = false },
    loadHistory() {
      this.loading = true
      this.loadError = false
      return getPersonalCabinetFeedback({ kind: this.kind })
        .then(({ data }) => { this.items = Array.isArray(data) ? data : (data.results || []) })
        .catch(() => { this.loadError = true })
        .finally(() => { this.loading = false })
    },
    submit() {
      this.attempted = true
      if (!this.valid || this.submitting) return
      this.submitting = true
      this.$refs.submit.preload = true
      const payload = { kind: this.kind, text: this.form.text }
      if (!this.isIdea) payload.category = this.form.category
      return postPersonalCabinetFeedback(payload)
        .then(({ data }) => { this.items.unshift(data); this.form = { category: '', text: '' }; this.attempted = false; this.closeForm(); this.$root.$emit('personal-cabinet:requests-changed'); this.$notify({ type: 'success', title: this.$t(this.isIdea ? 'feedback.ideaSubmitted' : 'feedback.reportSubmitted') }) })
        .finally(() => {
          this.submitting = false
          if (this.$refs.submit) this.$refs.submit.preload = false
        })
    },
    detailRoute(item) { return { name: 'personal-cabinet-request-detail', params: { requestId: `${this.kind}-${item.id}` }, query: { source: this.kind, sourceId: item.id } } },
    categoryName(category) { return this.$choice('categories', category) },
    formatDate(value) { return value ? formatLocalizedDate(value, { day: '2-digit', month: 'short', year: 'numeric' }) : '—' },
  },
}
</script>

<style lang="sass" scoped>
.feedback-form
  margin-bottom: 0
.feedback-page__action
  margin-bottom: 24px
.feedback-page__notice
  margin: -8px 0 16px
  padding: 13px 15px
  border-radius: 12px
  background: #f4efe1
  color: #6f674f
  font-size: 12px
  line-height: 1.45
.feedback-history
  margin-top: 28px
  > h2
    margin-bottom: 12px
    color: #6c6a62
    font-size: 10px
    font-weight: 700
    letter-spacing: .13em
    text-transform: uppercase
.feedback-card
  display: block
  margin-top: 10px
  padding: 16px
  border: 1px solid #e8e2d6
  border-radius: 18px
  background: #fffefa
  color: #34352e
  > div
    display: flex
    align-items: center
    justify-content: space-between
    gap: 12px
  small
    color: #a09c93
    font-size: 12px
  > strong
    display: block
    margin-top: 11px
    color: #8c7d35
    font-size: 12px
    text-transform: uppercase
  p
    display: -webkit-box
    margin-top: 10px
    font-family: 'Mazzard Soft H', 'SFUIText', sans-serif
    font-size: 16px
    font-weight: 500
    line-height: 1.4
    -webkit-line-clamp: 3
    -webkit-box-orient: vertical
    overflow: hidden
  > span
    display: block
    margin-top: 12px
    color: #8d7e34
    font-size: 12px
    font-weight: 700
</style>
